import { Component } from "@theme/component";

export class PersonalizeTextInputComponent extends Component {
  /** @type {HTMLElement | null} */
  container = null;
  /** @type {NodeListOf<HTMLElement> | null} */
  fontOptions = null;
  /** @type {HTMLTextAreaElement | null} */
  modalTextInput = null;
  /** @type {HTMLElement | null} */
  modalCharCount = null;
  /** @type {HTMLElement | null} */
  textPreviewArea = null;
  /** @type {HTMLElement | null} */
  previewText = null;
  /** @type {string} */
  selectedFont = "";

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanup();
  }

  init() {
    this.cacheElements();
    this.bindEvents();
    this.initializeDefaults();
  }

  cacheElements() {
    // Get the parent container that holds all the elements
    this.container = this.closest("[data-block-id]") || this;

    // Get font option elements
    this.fontOptions = this.querySelectorAll(".dialog-font-option");

    // Get text input elements
    this.modalTextInput = this.querySelector("[data-modal-text-input]");
    this.modalCharCount = this.querySelector("[data-modal-char-count]");

    // Get preview elements
    this.textPreviewArea = this.querySelector("[data-text-preview]");
    this.previewText = this.querySelector("[data-preview-text]");
  }

  bindEvents() {
    // Bind font option events
    if (this.fontOptions) {
      this.fontOptions.forEach((option) => {
        option.addEventListener("click", (e) =>
          this.handleFontSelection(e, option)
        );
      });
    }

    // Bind text input events
    if (this.modalTextInput) {
      this.modalTextInput.addEventListener("input", () =>
        this.handleTextInput()
      );
      this.modalTextInput.addEventListener("blur", () =>
        this.updateTextPreview()
      );
    }
  }

  initializeDefaults() {
    // Set initial selected font
    const firstSelectedOption = this.querySelector(
      '.dialog-font-option input[type="radio"]:checked'
    );
    if (firstSelectedOption) {
      const radioInput = /** @type {HTMLInputElement} */ (firstSelectedOption);
      this.selectedFont =
        radioInput.getAttribute("data-font-name") || radioInput.value || "";
    }

    // Initialize character count
    this.updateCharacterCount();
  }

  /**
   * @param {Event} e
   * @param {HTMLElement} option
   */
  handleFontSelection(e, option) {
    if (!this.fontOptions) return;

    const radioInput = option.querySelector('input[type="radio"]');
    const displayDiv = option.querySelector(".font-option-display");

    if (!radioInput || !displayDiv) return;

    // Remove selected class from all options
    this.fontOptions.forEach(
      /** @param {HTMLElement} opt */ (opt) => {
        const display = opt.querySelector(".font-option-display");
        if (display) {
          display.classList.remove("selected");
        }
      }
    );

    // Add selected class to clicked option
    displayDiv.classList.add("selected");

    // Check the radio input
    const inputElement = /** @type {HTMLInputElement} */ (radioInput);
    inputElement.checked = true;

    // Update selected font
    this.selectedFont =
      inputElement.getAttribute("data-font-name") || inputElement.value || "";

    // Update preview
    this.updateTextPreview();

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent("font-selected", {
        detail: {
          font: this.selectedFont,
          fontElement: radioInput,
        },
        bubbles: true,
      })
    );
  }

  handleTextInput() {
    this.updateCharacterCount();
    this.updateTextPreview();

    // Dispatch custom event
    if (this.modalTextInput) {
      this.dispatchEvent(
        new CustomEvent("text-input-changed", {
          detail: {
            text: this.modalTextInput.value,
            length: this.modalTextInput.value.length,
          },
          bubbles: true,
        })
      );
    }
  }

  updateCharacterCount() {
    if (!this.modalTextInput || !this.modalCharCount) return;

    const length = this.modalTextInput.value.length;
    this.modalCharCount.textContent = length.toString();

    // Add visual feedback for character limit
    const maxLength = parseInt(
      this.modalTextInput.getAttribute("maxlength") || "160"
    );
    const isNearLimit = length >= maxLength * 0.9;
    const isAtLimit = length >= maxLength;

    if (this.modalCharCount.parentElement) {
      const parent = this.modalCharCount.parentElement;
      parent.classList.toggle("near-limit", isNearLimit && !isAtLimit);
      parent.classList.toggle("at-limit", isAtLimit);
    }
  }

  updateTextPreview() {
    if (!this.modalTextInput || !this.previewText || !this.textPreviewArea)
      return;

    const text = this.modalTextInput.value.trim();

    if (text) {
      this.previewText.textContent = text;
      // for showing text preview 
      // this.textPreviewArea.style.display = "block";

      // Apply font styling if available
      if (this.selectedFont) {
        this.previewText.setAttribute("data-font", this.selectedFont);
      }
    } else {
      this.textPreviewArea.style.display = "none";
    }
  }

  cleanup() {
    // Clean up any event listeners if needed
    if (this.modalTextInput) {
      this.modalTextInput.removeEventListener("input", this.handleTextInput);
      this.modalTextInput.removeEventListener("blur", this.updateTextPreview);
    }
  }

  // Public API methods

  /**
   * @returns {string}
   */
  getText() {
    return this.modalTextInput?.value || "";
  }

  /**
   * @param {string} text
   */
  setText(text) {
    if (this.modalTextInput) {
      this.modalTextInput.value = text;
      this.updateCharacterCount();
      this.updateTextPreview();
    }
  }

  /**
   * @returns {string}
   */
  getSelectedFont() {
    return this.selectedFont;
  }

  /**
   * @param {string} fontName
   */
  setSelectedFont(fontName) {
    if (!this.fontOptions) return;

    this.fontOptions.forEach(
      /** @param {HTMLElement} option */ (option) => {
        const radioInput = option.querySelector('input[type="radio"]');
        const displayDiv = option.querySelector(".font-option-display");

        if (!radioInput || !displayDiv) return;

        const inputElement = /** @type {HTMLInputElement} */ (radioInput);
        const optionFontName =
          inputElement.getAttribute("data-font-name") || inputElement.value;

        if (optionFontName === fontName) {
          // Select this option
          displayDiv.classList.add("selected");
          inputElement.checked = true;
          this.selectedFont = fontName;
        } else {
          // Deselect other options
          displayDiv.classList.remove("selected");
          inputElement.checked = false;
        }
      }
    );

    this.updateTextPreview();
  }

  /**
   * Clear all text and reset to defaults
   */
  clearText() {
    this.setText("");
    if (this.textPreviewArea) {
      this.textPreviewArea.style.display = "none";
    }
  }

  /**
   * @returns {number}
   */
  getCharacterCount() {
    return this.getText().length;
  }

  /**
   * @returns {number}
   */
  getMaxLength() {
    return parseInt(this.modalTextInput?.getAttribute("maxlength") || "160");
  }

  /**
   * @returns {number}
   */
  getRemainingCharacters() {
    return this.getMaxLength() - this.getCharacterCount();
  }

  /**
   * @returns {boolean}
   */
  isAtCharacterLimit() {
    return this.getCharacterCount() >= this.getMaxLength();
  }

  /**
   * @returns {boolean}
   */
  hasText() {
    return this.getText().trim().length > 0;
  }

  /**
   * @returns {boolean}
   */
  isValid() {
    const hasRequiredText = this.modalTextInput?.hasAttribute("required")
      ? this.hasText()
      : true;
    const isWithinLimit = this.getCharacterCount() <= this.getMaxLength();
    return hasRequiredText && isWithinLimit;
  }

  /**
   * Focus the text input
   */
  focus() {
    if (this.modalTextInput) {
      this.modalTextInput.focus();
    }
  }

  /**
   * Get validation status and errors
   * @returns {{isValid: boolean, errors: string[]}}
   */
  validate() {
    const errors = [];

    if (this.modalTextInput?.hasAttribute("required") && !this.hasText()) {
      errors.push("Text input is required");
    }

    if (this.getCharacterCount() > this.getMaxLength()) {
      errors.push(
        `Text exceeds maximum length of ${this.getMaxLength()} characters`
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

if (!customElements.get("personalize-text-input-component")) {
  customElements.define(
    "personalize-text-input-component",
    PersonalizeTextInputComponent
  );
}
